<template>
  <div class="passenger-form" v-if="fields.length > 0">
    <button
      type="button"
      class="passenger-form__header"
      @click="$emit('toggle')"
    >
      <passenger-icon
        :passenger="passenger"
        :color="sass('primary')"
        :size="18"
      />
      <span class="passenger-form__title">
        Passeggero {{ passenger_index + 1 }}
        <span class="passenger-form__age">({{ age_label }})</span>
      </span>
      <vb-icon
        class="passenger-form__status"
        :name="'check_circle'"
        :color="has_filled_required_info ? sass('success') : sass('gray-500')"
        :size="14"
      />
      <chevron
        class="passenger-form__chevron"
        icon="chevron-down"
        :size="14"
        :is_open="expanded"
      />
    </button>

    <b-collapse :id="collapse_id" :visible="expanded">
      <b-row>
        <template v-for="(field, field_index) in fields">
          <!-- Gender -->
          <div
            v-if="field.name === 'gender'"
            :class="field_cols(field)"
            :key="`field_static_${field.name}_${passenger_index}_${field_index}`"
          >
            <validation-provider
              ref="gender_provider"
              :rules="field.validate"
              :name="field.label"
              v-slot="{ errors }"
              :vid="`vvp_static_${passenger_index}_${field_index}`"
            >
              <label class="passenger-form__label">
                <span>{{ field.label }}</span>
                <vb-icon
                  v-if="field.tooltip"
                  :name="'info'"
                  :color="sass('gray-500')"
                  :size="14"
                  class="passenger-form__tooltip-icon"
                  v-b-popover.hover.top="field.tooltip"
                />
              </label>
              <div class="passenger-form__select">
                <vb-select
                  :class="field.name"
                  v-model="field.value"
                  :vb_options="{
                    options: field.options,
                    persistent_key: build_input_id(passenger, field.name),
                  }"
                />
              </div>
              <span class="vee--errors">{{ errors[0] }}</span>
            </validation-provider>
          </div>

          <!-- Name -->
          <div
            v-else-if="field.name === 'name'"
            :class="field_cols(field)"
            :key="`field_static_name_${passenger_index}_${field_index}`"
          >
            <validation-provider
              :rules="field.validate"
              :name="field.label"
              v-slot="{ errors }"
              :vid="`vvp_static_${passenger_index}_${field_index}`"
            >
              <label class="passenger-form__label">
                <span>{{ field.label }}</span>
                <vb-icon
                  v-if="field.tooltip"
                  :name="'info'"
                  :color="sass('gray-500')"
                  :size="14"
                  class="passenger-form__tooltip-icon"
                  v-b-popover.hover.top="field.tooltip"
                />
              </label>
              <vb-input
                v-model="field.value"
                :vb_options="{
                  placeholder: field.placeholder,
                  autocomplete: field.autocomplete,
                  name: field.name,
                  persistent_key: build_input_id(passenger, field.name),
                }"
              />
              <span class="vee--errors">{{ errors[0] }}</span>
            </validation-provider>
          </div>

          <!-- Surname -->
          <div
            v-else-if="field.name === 'surname'"
            :class="field_cols(field)"
            :key="`field_static_lastname_${passenger_index}_${field_index}`"
          >
            <validation-provider
              :rules="field.validate"
              :name="field.label"
              v-slot="{ errors }"
              :vid="`vvp_static_${passenger_index}_${field_index}`"
            >
              <label class="passenger-form__label">
                <span>{{ field.label }}</span>
                <vb-icon
                  v-if="field.tooltip"
                  :name="'info'"
                  :color="sass('gray-500')"
                  :size="14"
                  class="passenger-form__tooltip-icon"
                  v-b-popover.hover.top="field.tooltip"
                />
              </label>
              <vb-input
                v-model="field.value"
                :vb_options="{
                  placeholder: field.placeholder,
                  autocomplete: field.autocomplete,
                  name: field.name,
                  persistent_key: build_input_id(passenger, field.name),
                }"
              />
              <span class="vee--errors">{{ errors[0] }}</span>
            </validation-provider>
          </div>

          <!-- Birthday -->
          <div
            v-else-if="field.name === 'birthday'"
            :class="field_cols(field)"
            :key="`field_static_birthdate_${passenger_index}_${field_index}`"
          >
            <validation-provider
              :rules="field.validate"
              :name="field.label"
              v-slot="{ errors }"
              :vid="`vvp_static_${passenger_index}_${field_index}`"
            >
              <label class="passenger-form__label">
                <span>{{ field.label }}</span>
                <vb-icon
                  v-if="field.tooltip"
                  :name="'info'"
                  :color="sass('gray-500')"
                  :size="14"
                  class="passenger-form__tooltip-icon"
                  v-b-popover.hover.top="field.tooltip"
                />
              </label>
              <div class="passenger-form__calendar">
                <birthdate-input
                  v-model="field.value"
                  :min="field.calendar.min"
                  :max="field.calendar.max"
                  :input_id="build_input_id(passenger, field.name)"
                  :persistent_key="build_input_id(passenger, field.name)"
                  :corrected="!!birthdate_correction(correction_key)"
                  @corrected="
                    (correction) =>
                      notify_birthdate_correction(
                        correction_key,
                        correction,
                        declared_age
                      )
                  "
                />
              </div>
              <span class="vee--errors">{{ errors[0] }}</span>
            </validation-provider>
          </div>

          <!-- Generic required fields -->
          <div
            :class="field_cols(field)"
            :key="`field_${passenger_index}_${field_index}`"
            v-else-if="field.validate.required && !field.hidden"
          >
            <validation-provider
              :rules="field.validate"
              :name="field.label"
              v-slot="{ errors }"
              :vid="`vvp_${passenger_index}_${field_index}`"
            >
              <label class="passenger-form__label">
                <span>{{ field.label }}</span>
                <vb-icon
                  v-if="field.tooltip"
                  :name="'info'"
                  :color="sass('gray-500')"
                  :size="14"
                  class="passenger-form__tooltip-icon"
                  v-b-popover.hover.top="field.tooltip"
                />
              </label>

              <div
                v-if="field.type === 'calendar'"
                class="passenger-form__calendar"
              >
                <birthdate-input
                  v-model="field.value"
                  :min="field.calendar.min"
                  :max="field.calendar.max"
                  :input_id="build_input_id(passenger, field.name)"
                  :persistent_key="build_input_id(passenger, field.name)"
                  :corrected="!!birthdate_correction(correction_key)"
                  @corrected="
                    (correction) =>
                      notify_birthdate_correction(
                        correction_key,
                        correction,
                        declared_age
                      )
                  "
                />
              </div>

              <vb-input
                v-else-if="field.type === 'text'"
                :key="`input_${passenger_index}_${field_index}`"
                v-model="field.value"
                :vb_options="{
                  placeholder: field.placeholder,
                  autocomplete: field.autocomplete,
                  name: field.name,
                  persistent_key: build_input_id(passenger, field.name),
                }"
              />

              <div
                v-else-if="field.type === 'select'"
                class="passenger-form__select"
              >
                <vb-select
                  :class="field.name === 'type_pax' ? 'vb-select--readonly' : ''"
                  :key="`select_${passenger_index}_${field_index}`"
                  v-model="field.value"
                  :disabled="field.disabled"
                  :vb_options="{
                    options: field.options,
                    persistent_key:
                      field.name !== 'type_pax'
                        ? build_input_id(passenger, field.name)
                        : undefined,
                  }"
                /><span
                  v-if="field.name === 'type_pax'"
                  v-html="
                    passenger.age < 30
                      ? `(${passenger.age} ${passenger.age > 1 ? 'anni' : 'anno'})`
                      : ''
                  "
                ></span>
              </div>

              <vb-checkbox
                v-else-if="field.type === 'checkbox'"
                v-model="field.value"
                :vb_options="{ text: field.placeholder }"
              />

              <span class="vee--errors">{{ errors[0] }}</span>
            </validation-provider>
          </div>
        </template>
      </b-row>
    </b-collapse>
  </div>
</template>

<script>
import { build_store_key } from "@/utilities/persistent-inputs";
import { DEFAULT_ADULT_AGE } from "@resources/passenger";
import { is_child, is_infant } from "@alpitour/utilities";
import { birthdate_correction_mixin } from "@/utilities/birthdate-correction";
import BirthdateInput from "@components/blocks/birthdate-input.vue";
import PassengerIcon from "@alpitour/components/atoms/passenger-icon.vue";
import Chevron from "@components/blocks/chevron.vue";

const FIELD_COLS = {
  gender: "col-6 col-lg-2",
  name: "col-12 col-lg-3",
  surname: "col-12 col-lg-3",
  birthday: "col-12 col-lg-4",
  nationality: "col-12 col-lg-2",
  prefix_phone: "col-6 col-lg-2",
  phone: "col-6 col-lg-5",
};

const DEFAULT_FIELD_COLS = "col-12 col-lg-3";

export default {
  name: "passenger-form",
  components: {
    "birthdate-input": BirthdateInput,
    "passenger-icon": PassengerIcon,
    chevron: Chevron,
  },
  props: {
    passenger: {
      type: Object,
      required: true,
    },
    passenger_index: {
      type: Number,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [birthdate_correction_mixin],
  computed: {
    collapse_id() {
      return `pax-${this.correction_key}`;
    },
    correction_key() {
      const { id } = this.passenger || {};
      return String(id !== undefined && id !== null ? id : this.passenger_index);
    },
    has_filled_required_info() {
      const required = this.fields.filter(
        (field) => field.validate && field.validate.required && !field.hidden,
      );
      if (required.length === 0) return false;
      return required.every(
        (field) => field.value !== undefined && field.value !== null && field.value !== "",
      );
    },
    // Solo quando l'eta' restringe i bound a un anno: gli adulti (compreso il
    // default a 30) tengono la fascia larga, quindi non c'e' un'eta' da citare.
    declared_age() {
      const { age } = this.passenger || {};
      if (typeof age !== "number" || age >= DEFAULT_ADULT_AGE || age >= 18) {
        return null;
      }
      return age;
    },
    fields() {
      const { details = {} } = this.passenger || {};
      return details.fields || [];
    },
    age_label() {
      if (is_infant(this.passenger)) return "neonato";
      if (is_child(this.passenger)) return "bambino";
      return "adulto";
    },
    passenger_name() {
      return (this.fields.find((f) => f.name === "name") || {}).value;
    },
    passenger_surname() {
      return (this.fields.find((f) => f.name === "surname") || {}).value;
    },
    passenger_gender() {
      return (this.fields.find((f) => f.name === "gender") || {}).value;
    },
  },
  watch: {
    passenger_name() {
      this.maybe_trigger_gender_validation();
    },
    passenger_surname() {
      this.maybe_trigger_gender_validation();
    },
  },
  methods: {
    build_store_key,
    field_cols(field) {
      const { name = "" } = field || {};
      return `passenger-form__field ${FIELD_COLS[name] || DEFAULT_FIELD_COLS}`;
    },
    build_input_id(passenger, field_name) {
      return this.build_store_key(
        passenger.id,
        passenger.passengerType,
        field_name,
      );
    },
    maybe_trigger_gender_validation() {
      if (
        this.passenger_name &&
        this.passenger_surname &&
        !this.passenger_gender
      ) {
        this.$nextTick(() => {
          const ref = this.$refs.gender_provider;
          const provider = Array.isArray(ref) ? ref[0] : ref;
          if (provider && typeof provider.validate === "function") {
            provider.validate();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.passenger-form {
  padding: $grid-gutter-20 0;
  border-bottom: 1px solid $gray-200;

  &__header {
    display: flex;
    align-items: center;
    gap: $grid-gutter-width/6;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    text-align: left;
    color: $gray-900;
    font-weight: $vb-font-weight-semibold;
    cursor: pointer;
  }

  &__chevron {
    margin-left: auto;
  }

  &__age {
    text-transform: lowercase;
  }

  &__status,
  &__chevron {
    flex-shrink: 0;
  }

  &__title {
    flex-shrink: 1;
  }

  &__field {
    margin-top: $grid-gutter-width/2;
  }

  &__label {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: $grid-gutter-width / 3;
    font-size: $font-size-md;
  }

  &__select {
    display: flex;
    align-items: center;

    .vb-select--readonly {
      width: auto;
      padding-right: $grid-gutter-width/10;
    }

    span {
      white-space: nowrap;
    }
  }
}
</style>
